import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  Alert,
  Paper,
  Text,
  type ResourceQuery,
} from '@vipso/shared';
import { useListProductsQuery, type Product } from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';

export function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });

  const { data, isLoading, isFetching, isError } = useListProductsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });

  const list = data?.listProducts
    ? {
        rows: (data.listProducts.rows ?? []) as Product[],
        total: data.listProducts.total ?? 0,
        page: query.page,
        pageLimit: query.pageLimit,
      }
    : undefined;

  return (
    <Stack gap="md">
      <PageHeader title={t('products.title')} description={t('products.description')} />
      {isError ? <Alert color="red" title={t('products.loadFailed')} /> : null}
      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('products.searchPlaceholder')}
        emptyTitle={t('products.emptyTitle')}
        emptyDescription={t('products.emptyDescription')}
        testId="product-table"
        mobileView="grid"
        addButton={
          canMutate() ? (
            <Button testId="product-add" onClick={() => navigate('/products/new')}>
              {t('products.add')}
            </Button>
          ) : undefined
        }
        onRowClick={(row) => navigate(`/products/${row.id}`)}
        columns={[
          { key: 'name', header: t('common.name'), render: (row) => row.name },
          { key: 'unit', header: t('products.unit'), render: (row) => row.unit || t('common.emptyValue') },
          {
            key: 'category',
            header: t('products.category'),
            render: (row) => row.category?.name || t('common.emptyValue'),
          },
          {
            key: 'approval',
            header: t('approval.status'),
            render: (row) => <ApprovalStatusBadge status={row.approvalStatus} />,
          },
          {
            key: 'price',
            header: t('products.price'),
            render: (row) =>
              row.pricePerQuantity != null
                ? `₹${Number(row.pricePerQuantity).toFixed(2)}`
                : t('common.emptyValue'),
          },
        ]}
        renderCard={(row: Product) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between">
                <Text fw={600}>{row.name}</Text>
                <ApprovalStatusBadge status={row.approvalStatus} />
              </Group>
              <Text size="sm" c="dimmed">
                {[row.unit, row.category?.name].filter(Boolean).join(' · ') || t('common.emptyValue')}
              </Text>
              {row.pricePerQuantity != null ? (
                <Text size="sm" fw={600}>
                  ₹{Number(row.pricePerQuantity).toFixed(2)}
                </Text>
              ) : null}
            </Stack>
          </Paper>
        )}
      />
    </Stack>
  );
}
